import type { CSSProperties } from 'react';

export const QUEST_GLITCH_MS = 1500;

type GlitchFrame = {
  transform: string;
  filter: string;
  scanY: number;
  scanOpacity: number;
  clipTop: number;
  clipBottom: number;
  rgbShift: number;
};

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function pickFilter(): string {
  const roll = Math.random();

  if (roll < 0.38) {
    const amount = randomBetween(0.4, 1);
    const saturate = randomBetween(1, 1.25);
    return `invert(${amount.toFixed(2)}) hue-rotate(180deg) contrast(${randomBetween(1.02, 1.12).toFixed(2)}) saturate(${saturate.toFixed(2)})`;
  }

  if (roll < 0.52) {
    return `invert(${randomBetween(0.15, 0.45).toFixed(2)}) hue-rotate(${randomBetween(160, 200).toFixed(0)}deg)`;
  }

  if (roll < 0.64) {
    return `hue-rotate(${randomBetween(-14, 14).toFixed(1)}deg) saturate(${randomBetween(1.05, 1.35).toFixed(2)}) contrast(${randomBetween(1, 1.1).toFixed(2)})`;
  }

  if (roll < 0.72) {
    return `brightness(${randomBetween(0.88, 1.12).toFixed(2)}) contrast(${randomBetween(1.05, 1.2).toFixed(2)})`;
  }

  return 'none';
}

export function createRandomGlitchFrame(): GlitchFrame {
  const spike = Math.random() < 0.22;
  const x = spike ? randomBetween(-48, 48) : randomBetween(-32, 32);
  const y = spike ? randomBetween(-36, 36) : randomBetween(-24, 24);
  const skew = randomBetween(-1.4, 1.4);
  const scale = Math.random() < 0.18 ? randomBetween(0.985, 1.018) : 1;

  return {
    transform: `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) skewX(${skew.toFixed(2)}deg) scale(${scale.toFixed(3)})`,
    filter: pickFilter(),
    scanY: randomBetween(0, 10),
    scanOpacity: randomBetween(0.3, 0.72),
    clipTop: randomBetween(0, 46),
    clipBottom: randomBetween(0, 46),
    rgbShift: randomBetween(-18, 18),
  };
}

export function glitchFrameToStyle(frame: GlitchFrame): CSSProperties {
  return {
    transform: frame.transform,
    filter: frame.filter,
    ['--glitch-scan-y' as string]: `${frame.scanY.toFixed(1)}px`,
    ['--glitch-scan-opacity' as string]: frame.scanOpacity.toFixed(2),
    ['--glitch-clip-top' as string]: `${frame.clipTop.toFixed(1)}%`,
    ['--glitch-clip-bottom' as string]: `${frame.clipBottom.toFixed(1)}%`,
    ['--glitch-rgb-shift' as string]: `${frame.rgbShift.toFixed(1)}px`,
  };
}

export function nextGlitchDelayMs(): number {
  return Math.round(randomBetween(48, 132));
}

export function prefersReducedGlitchMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
